import 'firebase/firestore';

import { DocumentReference, DocumentSnapshot } from '@google-cloud/firestore';
import { destroy, flow, getRoot, Instance, onSnapshot, types } from 'mobx-state-tree';

import firebaseService from '../services/firebaseService';
import spotifyConnectService from '../services/spotifyConnectService';
import { UserRoles } from '../types/shared';
import spotifyService from './../services/spotifyService';
import QueueItemModel, { IQueueItemModel } from './models/QueueItemModel';
import TrackModel, { ITrackModel } from './models/TrackModel';
import { IRootStore } from './RootStore';

enum QueueDBActions {
    NONE = 'none',
    REMOVED = 'removed',
    ADDED = 'added',
}

const PlaybackConfigStore = types.model('PlaybackConfigStore', {
    isPlaying: types.optional(types.boolean, false),
    currentTrackId: types.optional(types.string, ''),
});

const PlaybackStore = types
    .model('PlaybackStore', {
        queue: types.array(QueueItemModel),
        backupTracks: types.map(
            types.safeReference(TrackModel, {
                acceptsUndefined: false,
            })
        ),
        config: types.optional(PlaybackConfigStore, {}),
        lastAction: types.optional(types.enumeration(Object.values(QueueDBActions)), QueueDBActions.NONE),
        lastTrackIdAddedOrRemoved: types.optional(types.string, ''),
        lastUserIdAction: types.maybeNull(types.string),
    })
    .volatile(() => ({
        isPlayerReady: false,
        queueUpdated: false,
        progress: 0,
        storeNextQueueChangesInDb: true,
    }))
    .views(self => ({
        get queueUris() {
            return self.queue.map(item => item.track.uri);
        },
        get currentTrack() {
            const queue = [...self.queue.slice()];
            console.log('queue:', queue);
            console.log('currentTrackId:', self.config.currentTrackId);
            console.log(this.queueItemsSortedByOrder);
            const trackFound = queue.find(item => item.track.id === self.config.currentTrackId);
            return trackFound;
        },
        trackAlreadyInQueue(trackToCheck: ITrackModel) {
            return !!self.queue.find(item => item.track.id === trackToCheck.id);
        },
        get queueItemsSortedByOrder() {
            return self.queue.slice().sort((a, b) => a.order - b.order);
        },
        get upNext() {
            return self.queue.slice()[1] || null;
        },
    }))
    .views(self => ({
        get nextOrderValue() {
            return self.queueItemsSortedByOrder.length
                ? self.queueItemsSortedByOrder[self.queueItemsSortedByOrder.length - 1].order + 1
                : 0;
        },
    }))
    .actions(self => {
        const rootStore: IRootStore = getRoot(self);

        const addToQueue: (track: ITrackModel, autoAdded?: boolean) => void = (track: ITrackModel, autoAdded = false) => {
            try {
                self.storeNextQueueChangesInDb = true;
                const queueItem: IQueueItemModel = {
                    user: !autoAdded ? rootStore.me.profile : null,
                    track,
                    order: self.nextOrderValue,
                };
                // TODO: figure out how re-render with only pushing an element
                self.queueUpdated = false;
                if (self.trackAlreadyInQueue(track)) {
                    rootStore.alerts.warning('Warning', 'This track is already in the queue');
                    return;
                }
                self.lastAction = QueueDBActions.ADDED;
                self.lastTrackIdAddedOrRemoved = queueItem.track.id;
                self.lastUserIdAction = queueItem.user ? queueItem.user.id : null;
                self.queue.push(queueItem);
                self.queueUpdated = true;
                rootStore.alerts.info('Track added', track.name);
            } catch (error) {
                console.log('error', error);
            }
        };

        const initPlayer = flow(function* initPlayer() {
            try {
                console.log('init player spotify access token', spotifyService.getAccessToken());
                yield spotifyConnectService.init(spotifyService.getAccessToken());
                self.isPlayerReady = true;
                console.log('isPlayerReady = true');
            } catch (error) {
                console.log('error', error);
            }
        });

        const setProgress = (value: number) => {
            self.progress = value;
        };

        const playTrack = flow(function*(track: ITrackModel) {
            // eslint-disable-next-line @typescript-eslint/camelcase
            yield spotifyService.play({ device_id: spotifyConnectService.deviceId, uris: [track.uri] });
            self.config.currentTrackId = track.id;
            self.config.isPlaying = true;
        });
        // TODO: the backup tracks sometimes are not loaded, I guess is when signing as a host after being signed in as a guest before
        const loadBackupTracks = flow(function*() {
            const response: SpotifyApi.UsersTopTracksResponse = yield spotifyService.getMyTopTracks();
            for (let i = 0, l = response.items.length; i < l; i++) {
                const track = response.items[i];
                const newTrack = rootStore.music.getTrack(track.id) || rootStore.music.addTrack(track);
                if (!self.queue.length && i === 0) {
                    console.log('added from loadBackupTracks');
                    addToQueue(newTrack, true);
                    playTrack(newTrack);
                }
                const alreadyInQueue = self.trackAlreadyInQueue(newTrack);
                if (alreadyInQueue && self.backupTracks.get(newTrack.id)) {
                    self.backupTracks.delete(newTrack.id);
                    continue;
                }
                if (!alreadyInQueue) {
                    self.backupTracks.set(newTrack.id, newTrack);
                }
            }
        });

        const playNext = () => {
            if (self.queue.length) {
                self.lastAction = QueueDBActions.REMOVED;
                self.lastTrackIdAddedOrRemoved = self.queue[0].track.id;
                self.lastUserIdAction = rootStore.me.profile ? rootStore.me.profile.id : '';
                self.storeNextQueueChangesInDb = true;
                self.queue.shift();
                playTrack(self.queue[0].track);
            }
        };
        // TODO: Auto added tracks are not firing firebase doc Snapshot (SEE COMMENT BELOW)
        const getNextTrack = flow(function*() {
            self.config.isPlaying = false;
            self.progress = 0;
            if (self.queue.length === 2) {
                loadBackupTracks();
            }
            if (self.queue.length > 1) {
                playNext();
                return;
            }
            const track = Array.from(self.backupTracks.values()).shift();
            if (track) {
                console.log('added from getNextTrack');
                addToQueue(track, true);
                self.backupTracks.delete(track.id);
                // TODO: this is needed to be able to trigger the 2 corresponding OnSnapshot changes
                // adding and removing.
                // Try removing first, adding after.
                yield new Promise(resolve => {
                    setTimeout(() => {
                        resolve();
                    }, 0);
                });
                playNext();
            }
        });

        const removeFromBackup = (track: ITrackModel) => {
            self.backupTracks.delete(track.id);
        };

        const pause = flow(function*() {
            // eslint-disable-next-line @typescript-eslint/camelcase
            yield spotifyService.pause({ device_id: spotifyConnectService.deviceId });
            self.config.isPlaying = false;
        });

        const resume = flow(function*() {
            // eslint-disable-next-line @typescript-eslint/camelcase
            yield spotifyService.play({ device_id: spotifyConnectService.deviceId });
            self.config.isPlaying = true;
        });

        // TODO: LastAction is not being updated when a track is auto added from backup tracks
        const storeQueueToDB = flow(function*() {
            console.log('Storing new queue in DB');
            console.log('self.storeNextQueueChangesInDb', self.storeNextQueueChangesInDb);
            if (self.storeNextQueueChangesInDb) {
                const queueData: IQueueDocument = {
                    lastAction: self.lastAction,
                    lastTrackIdAddedOrRemoved: self.lastTrackIdAddedOrRemoved,
                    lastUserIdAction: self.lastUserIdAction,
                    items: [...self.queue],
                };
                console.log('storing to DB', queueData);
                yield firebaseService.storeQueue(rootStore.config.uid, queueData);
            }
        });

        const storePlaybackInDB = flow(function*() {
            if (rootStore.auth.role === UserRoles.HOST) {
                console.log('storing plaback config');
                const playbackData: IPlaybackDocument = {
                    isPlaying: self.config.isPlaying,
                    currentTrackId: self.config.currentTrackId,
                };
                yield firebaseService.storePlayback(rootStore.config.uid, playbackData);
            }
        });

        const addQueueItemsFromDB = (items: IQueueItemModel[]) => {
            console.log('adding tracks from DB');
            self.storeNextQueueChangesInDb = false;
            items.forEach(item => {
                if (!self.trackAlreadyInQueue(item.track)) {
                    self.queue.push({
                        ...item,
                        track: rootStore.music.addTrackFromDB(item.track),
                        user: item.user ? rootStore.users.addUser(item.user) : null,
                    });
                }
            });
        };

        const addItemToQueueFromDB = (item: IQueueItemModel) => {
            // TODO: figure out how re-render with only pushing an element
            self.storeNextQueueChangesInDb = false;
            self.queue.push(item);
            self.queueUpdated = true;
        };

        const removeItemFromQueueFromDB = (itemId: string) => {
            self.storeNextQueueChangesInDb = false;
            const item = self.queue.find(item => item.track.id === itemId);
            console.log('self.queue', self.queue);
            if (item) {
                destroy(item);
                self.queueUpdated = true;
            }
            if (self.queue[0]) {
                self.config.currentTrackId = self.queue[0].track.id;
            }
        };

        const loadDataFromDB = flow(function* loadDataFromDB() {
            console.log('reading queue');
            console.log('reading plaback config');
            const playbackDoc: DocumentSnapshot<IPlaybackDocument> = yield firebaseService.loadPlayback(rootStore.config.uid);
            const queueDoc: DocumentSnapshot<IQueueDocument> = yield firebaseService.loadQueue(rootStore.config.uid);
            const playback = playbackDoc.data();
            const queue = queueDoc.data();
            if (playback) {
                self.config.currentTrackId = playback.currentTrackId;
                self.config.isPlaying = playback.isPlaying;
            }
            if (queue) {
                addQueueItemsFromDB(queue.items);
            }
        }) as () => Promise<void>;

        const afterAttach = () => {
            onSnapshot(self.queue, storeQueueToDB);

            onSnapshot(self.config, storePlaybackInDB);
        };

        return {
            loadBackupTracks,
            playTrack,
            initPlayer,
            pause,
            resume,
            playNext,
            addToQueue,
            getNextTrack,
            removeFromBackup,
            setProgress,
            afterAttach,
            loadDataFromDB,
            addQueueItemsFromDB,
            addItemToQueueFromDB,
            removeItemFromQueueFromDB,
        };
    })
    .actions(self => {
        // Realtime changes in queue
        const rootStore: IRootStore = getRoot(self);
        const startListenQueueChanges = flow(function*() {
            if (rootStore.auth.isAuthenticated) {
                const doc: DocumentReference = yield firebaseService.loadQueueDoc(rootStore.config.uid);
                doc.onSnapshot(qSnapshot => {
                    const docData = qSnapshot.data() as IQueueDocument | undefined;
                    if (docData) {
                        const myUserRole = rootStore.auth.role;
                        const myUserId = rootStore.me.profile?.id;
                        const otherUserAction =
                            (myUserRole === UserRoles.HOST &&
                                docData.lastUserIdAction !== null &&
                                docData.lastUserIdAction !== myUserId) ||
                            (myUserRole === UserRoles.GUEST &&
                                (docData.lastUserIdAction === null || docData.lastUserIdAction !== myUserId));

                        if (docData.lastAction === QueueDBActions.ADDED) {
                            const item = docData.items.find(item => item.track.id === docData.lastTrackIdAddedOrRemoved);
                            console.log('ADDED', item);
                            if (item && otherUserAction && !self.trackAlreadyInQueue(item.track)) {
                                self.addItemToQueueFromDB({
                                    ...item,
                                    track: rootStore.music.addTrackFromDB(item.track),
                                    user: item.user ? rootStore.users.addUser(item.user) : null,
                                });
                            }
                            return;
                        }
                        if (docData.lastAction === QueueDBActions.REMOVED) {
                            console.log('REMOVED', docData.lastTrackIdAddedOrRemoved);
                            if (otherUserAction) {
                                self.removeItemFromQueueFromDB(docData.lastTrackIdAddedOrRemoved);
                            }
                        }
                    }
                });
            }
        }) as () => Promise<void>;

        const stopListenQueueChanges = flow(function*() {
            const doc: DocumentReference = yield firebaseService.loadQueueDoc(rootStore.config.uid);
            const unsub = doc.onSnapshot(() => {});
            //Stop listening for changes
            unsub();
        }) as () => Promise<void>;

        const beforeDetach = flow(function*() {
            yield stopListenQueueChanges();
        });
        return {
            beforeDetach,
            startListenQueueChanges,
            stopListenQueueChanges,
        };
    });

export type IPlaybackStore = Instance<typeof PlaybackStore>;

export interface IPlaybackDocument {
    isPlaying: boolean;
    currentTrackId: string;
}

export interface IQueueDocument {
    items: IQueueItemModel[];
    lastTrackIdAddedOrRemoved: string;
    lastAction: QueueDBActions;
    lastUserIdAction: string | null;
}

export default PlaybackStore;
